'use client';

import getEnvValue from 'commons/getEnvValue';
import {
  getAppPostHogClient,
  setAppPostHogClient,
} from '@/joystagram-components/hooks/usePostHogFeatureFlag/appPostHogClient';
import { POSTHOG_HOST } from '../../config/postHogConstants';

const postHogClientInitState = {
  initPromise: null,
  hasInitialized: false,
  hasSeeded: false,
};

const getPostHogProjectToken = () =>
  getEnvValue('NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN') || '';

const hasSeededFlags = bootstrap =>
  Object.keys(bootstrap?.featureFlags || {}).length > 0;

// init() is the only way a seed reaches the SDK, so an already-initialized
// client (any navigation onto a PDP) needs it pushed in by hand. Without this
// the SDK reports a different variant than the page rendered.
const applySeedToLiveClient = (posthog, bootstrap) => {
  if (
    postHogClientInitState.hasSeeded ||
    !posthog?.featureFlags ||
    !hasSeededFlags(bootstrap)
  ) {
    return;
  }

  postHogClientInitState.hasSeeded = true;
  posthog.featureFlags.override(bootstrap.featureFlags);
  posthog.reloadFeatureFlags();
};

const buildInitConfig = ({
  bootstrap,
  shouldUseCookielessMode = false,
} = {}) => ({
  api_host: POSTHOG_HOST,
  bootstrap,
  disable_session_recording: true,
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: false,
  cookieless_mode: shouldUseCookielessMode ? 'on_reject' : undefined,
  persistence: shouldUseCookielessMode ? 'memory' : 'localStorage+cookie',
  advanced_disable_flags: false,
  loaded: posthogInstance => {
    setAppPostHogClient(posthogInstance);
    window.posthog = posthogInstance;
  },
});

export const isPostHogClientConfigured = () =>
  Boolean(getPostHogProjectToken());

export const ensurePostHogClientInitialized = async ({
  bootstrap,
  shouldUseCookielessMode = false,
} = {}) => {
  if (!isPostHogClientConfigured()) {
    return null;
  }

  if (postHogClientInitState.hasInitialized && getAppPostHogClient()) {
    applySeedToLiveClient(getAppPostHogClient(), bootstrap);
    return getAppPostHogClient();
  }

  if (!postHogClientInitState.initPromise) {
    postHogClientInitState.initPromise = import('posthog-js').then(
      ({ default: posthog }) => {
        if (postHogClientInitState.hasInitialized && getAppPostHogClient()) {
          return getAppPostHogClient();
        }

        posthog.init(
          getPostHogProjectToken(),
          buildInitConfig({ bootstrap, shouldUseCookielessMode })
        );
        postHogClientInitState.hasInitialized = true;

        // Passing a value in makes the SDK skip its first fetch. Every other
        // flag then stays undefined until it refreshes, five minutes later.
        if (hasSeededFlags(bootstrap)) {
          postHogClientInitState.hasSeeded = true;
          posthog.reloadFeatureFlags();
        }

        return posthog;
      }
    );
  }

  // An in-flight init may be the deferred one, which carries no seed.
  return postHogClientInitState.initPromise.then(posthog => {
    applySeedToLiveClient(posthog, bootstrap);

    return posthog;
  });
};

export default ensurePostHogClientInitialized;
